import { Link } from "react-router-dom";
import { ArrowRight, FileText, FolderOpen, Layers, LayoutGrid, RefreshCw } from "lucide-react";
import {
  useAdminGetExamsQuery,
  useAdminGetExamAnalyticsQuery,
} from "../../api/examsApi";

/**
 * Exam applications, browsed the same way as upgrade applications: pick an exam,
 * then a diet, then work the list. Counts come from the per-exam analytics
 * endpoint rather than pulling every application into the browser.
 */
function ExamCard({ exam }: { exam: any }) {
  const { data: analytics } = useAdminGetExamAnalyticsQuery({ examId: exam.id });

  const total = (analytics as any)?.totals?.applications ?? 0;
  const diets = ((analytics as any)?.by_diet ?? []).length;

  return (
    <Link
      to={`/exams/admin/applications/exams/${exam.id}`}
      state={{ examTitle: exam.title, examCode: exam.code }}
      className="group relative bg-white border border-gray-200 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-gray-200 hover:-translate-y-1 overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50 rounded-bl-[60px] -mr-8 -mt-8 transition-colors group-hover:bg-indigo-50" />
      <div className="relative z-10 flex flex-col h-full">
        <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
          <FolderOpen className="w-6 h-6" />
        </div>

        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="text-xl font-black text-gray-900 tracking-tight group-hover:text-indigo-600 transition-colors">
            {exam.code}
          </h3>
          <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-100">
            {total}
          </span>
          {!exam.is_active && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold border border-gray-200 uppercase">
              Inactive
            </span>
          )}
        </div>

        <p className="text-sm font-medium text-gray-500 leading-relaxed mb-6">{exam.title}</p>

        <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-900 transition-colors">
            {diets} diet{diets !== 1 ? "s" : ""} with applications
          </span>
          <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function AdminExamApplicationsPage() {
  const { data: exams = [], isLoading, isFetching, refetch } = useAdminGetExamsQuery();

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
      {/* Title row */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center">
            <FileText className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="space-y-1">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Exam Applications</h1>
            <p className="text-sm font-bold text-gray-500">
              Choose an exam to review its applications, diet by diet.
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-all shadow-sm self-start"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Exam grid */}
      {isLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-gray-50 border border-gray-100 rounded-[32px] animate-pulse" />
          ))}
        </div>
      ) : (exams as any[]).length === 0 ? (
        <div className="py-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-[40px] flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center shadow-sm mb-4">
            <LayoutGrid className="w-8 h-8 text-gray-300" />
          </div>
          <h2 className="text-xl font-black text-gray-900 uppercase">No exams</h2>
          <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">
            Create an exam before applications can be taken.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(exams as any[]).map((ex: any) => (
            <ExamCard key={ex.id} exam={ex} />
          ))}
        </div>
      )}

      {/* Info footer */}
      <div className="mt-6 p-8 bg-indigo-50/50 rounded-[32px] border border-indigo-100 flex items-start gap-4">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
          <Layers className="text-indigo-600 w-5 h-5" />
        </div>
        <div>
          <h4 className="text-xs font-black text-indigo-900 uppercase tracking-widest mb-1">
            Administrative Note
          </h4>
          <p className="text-xs font-bold text-indigo-700/70 leading-relaxed max-w-2xl">
            Applications belong to a diet, and a diet belongs to one exam — so they are grouped the same
            way here. Open an exam to see its diets, then a diet to search, filter and act on its
            candidates.
          </p>
        </div>
      </div>
    </div>
  );
}
